import type { AgreementRow } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/AgreementsModal';
import type { ViewTrinityContractRow } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/ViewTrinityContractModal';
import type { PreContractProduct } from './preContractTypes';

const PRODUCT_TYPE_CODES: Record<PreContractProduct, readonly string[]> = {
    Solar: ['S', 'SB', 'SBR'],
    Roof: ['RO', 'R', 'RRR'],
    Battery: ['BO', 'B', 'SB', 'SBR'],
    RR: ['RR', 'RRR'],
    RRR: ['RRR'],
};

const OPPORTUNITY_PRODUCT_MATCH: Record<PreContractProduct, (name: string) => boolean> = {
    Solar: (name) => /^solar$/i.test(name),
    Roof: (name) => /^roof$/i.test(name) || name.toLowerCase() === 'roof only',
    Battery: (name) => /^battery$/i.test(name) || name.toLowerCase() === 'battery only',
    RR: (name) =>
        /^rr$/i.test(name) ||
        (name.toLowerCase().includes('remove') && name.toLowerCase().includes('reinstall')),
    RRR: (name) => /^rrr$/i.test(name),
};

export type SignedProductAgreement = {
    product: PreContractProduct;
    row: AgreementRow;
};

function financePartnerKey(name: string): string {
    return String(name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

function readOpportunityProductRows(opportunityDetails: unknown): Record<string, unknown>[] {
    const od = opportunityDetails as Record<string, unknown> | null | undefined;
    if (!od) return [];
    const nested = (od.OpportunityDetails ?? od.opportunityDetails) as Record<string, unknown> | undefined;
    const raw =
        od.OpportunityProduct ??
        od.opportunityProduct ??
        nested?.OpportunityProduct ??
        nested?.opportunityProduct ??
        od.OpportunityProductsFinanceInfo ??
        od.opportunityProductsFinanceInfo ??
        nested?.OpportunityProductsFinanceInfo ??
        [];
    if (!Array.isArray(raw)) return [];
    return raw.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
}

function partnerNameFromOpportunityRow(row: Record<string, unknown>): string {
    return String(
        row.Finance_Partner_Display_Name ??
            row.FinancePartnerDisplayName ??
            row.Finance_Partner ??
            row.finance_Partner ??
            '',
    ).trim();
}

function preContractProductMatchesFinanceCell(product: PreContractProduct, cellProduct: string): boolean {
    const value = cellProduct.trim().toLowerCase();
    if (product === 'Solar') return value === 'solar';
    if (product === 'Roof') return value === 'roof';
    if (product === 'Battery') return value === 'battery';
    if (product === 'RR') return value === 'rr' || value.includes('remove');
    if (product === 'RRR') return value === 'rrr';
    return false;
}

export function resolveFinancePartnerKeyForProduct(
    opportunityDetails: unknown,
    selectedFinanceCells: Set<string> | null | undefined,
    product: PreContractProduct,
): string | null {
    const matcher = OPPORTUNITY_PRODUCT_MATCH[product];

    for (const row of readOpportunityProductRows(opportunityDetails)) {
        if (row.ActiveFlag === false || row.activeFlag === false) continue;
        const typeName = String(row.Product_Type_Name ?? row.ProductTypeName ?? '').trim();
        if (!typeName || !matcher(typeName)) continue;
        const partner = partnerNameFromOpportunityRow(row);
        if (partner) return financePartnerKey(partner);
    }

    if (selectedFinanceCells) {
        for (const cellKey of selectedFinanceCells) {
            const [partnerPart, productPart = ''] = cellKey.split('::');
            if (!preContractProductMatchesFinanceCell(product, productPart)) continue;
            const partner = partnerPart?.trim();
            if (partner) return financePartnerKey(partner);
        }
    }

    return null;
}

function parseContractId(raw: string | undefined): number {
    return parseInt(String(raw || '').replace(/\D/g, ''), 10);
}

export function pickSignedAgreementRowForProduct(
    rows: AgreementRow[],
    product: PreContractProduct,
    expectedPartnerKey: string | null,
): AgreementRow | null {
    if (!expectedPartnerKey) return null;

    const codes = new Set(PRODUCT_TYPE_CODES[product].map((code) => code.toUpperCase()));
    const signedMatches = rows.filter((row) => {
        const code = String(row.productTypeCode || row.productType || '')
            .trim()
            .toUpperCase();
        if (!codes.has(code)) return false;
        if (!isAgreementRowSigned(row)) return false;
        const rowKey = financePartnerKey(row.financePartnerRaw || row.financePartner);
        return rowKey === expectedPartnerKey;
    });
    if (!signedMatches.length) return null;

    const latestFlagged = signedMatches.filter((row) => row.isLatestContractForProductType);
    const pool = latestFlagged.length ? latestFlagged : signedMatches;
    return [...pool].sort(
        (a, b) => parseContractId(b.contractIdRaw) - parseContractId(a.contractIdRaw),
    )[0];
}

export function buildSignedProductAgreements(
    selectedProducts: PreContractProduct[],
    agreementRows: AgreementRow[],
    opportunityDetails: unknown,
    selectedFinanceCells: Set<string> | null | undefined,
): SignedProductAgreement[] {
    const results: SignedProductAgreement[] = [];

    for (const product of selectedProducts) {
        const partnerKey = resolveFinancePartnerKeyForProduct(
            opportunityDetails,
            selectedFinanceCells,
            product,
        );
        const row = pickSignedAgreementRowForProduct(agreementRows, product, partnerKey);
        if (row) {
            results.push({ product, row });
        }
    }

    return results;
}

export function pickLatestAgreementRowForProduct(
    rows: AgreementRow[],
    product: PreContractProduct,
): AgreementRow | null {
    const codes = new Set(PRODUCT_TYPE_CODES[product].map((code) => code.toUpperCase()));
    const matches = rows.filter((row) => {
        const code = String(row.productTypeCode || row.productType || '')
            .trim()
            .toUpperCase();
        return codes.has(code);
    });
    if (!matches.length) return null;

    const latestFlagged = matches.filter((row) => row.isLatestContractForProductType);
    const pool = latestFlagged.length ? latestFlagged : matches;
    return [...pool].sort(
        (a, b) => parseContractId(b.contractIdRaw) - parseContractId(a.contractIdRaw),
    )[0];
}

export function quoteDetailsProductLabelFromRow(row: AgreementRow): string {
    const pt = (row.productTypeCode || row.productType || '').toUpperCase();
    if (pt === 'BO' || pt === 'B') return 'Battery Only';
    if (pt === 'SB') return 'Solar, Battery';
    if (pt === 'SBR') return 'Solar, Battery, Roof';
    if (pt === 'RO' || pt === 'R') return 'Roof Only';
    if (pt === 'RR') return 'Roof';
    if (pt === 'RRR') return 'Remove, Roof & Reinstall';
    return 'Solar';
}

export function isAgreementRowSigned(row: AgreementRow): boolean {
    if (row.isContractSignedBool) return true;
    const status = String(row.contractStatus || '').trim().toLowerCase();
    return status === 'signed';
}

export function agreementStatusLabel(row: AgreementRow | null): string {
    if (!row) return 'No agreement';
    if (isAgreementRowSigned(row)) return 'Signed';
    const contractStatus = String(row.contractStatus || '').trim();
    if (contractStatus && contractStatus !== '—') return contractStatus;
    const signStatus = String(row.agreementSignStatus || '').trim();
    if (signStatus && signStatus !== '—') return signStatus;
    return 'Pending signature';
}

export function contractDocumentUrl(row: AgreementRow | null): string | null {
    if (!row) return null;
    const external = row.externalLink?.trim();
    if (external) return external;
    const partner = row.partnerFileUrl?.trim();
    return partner || null;
}

export function agreementRowToTrinityRow(row: AgreementRow): ViewTrinityContractRow {
    return {
        key: row.key,
        quoteIdRaw: row.quoteIdRaw,
        agreementIdRaw: row.agreementIdRaw,
        partnerFileUrl: row.partnerFileUrl,
        productTypeCode: row.productTypeCode,
        productType: row.productType,
        financePartner: row.financePartner,
        financePartnerRaw: row.financePartnerRaw,
        financeMethod: row.financeMethod,
        loadCalculatorDataExists: row.loadCalculatorDataExists,
        manualOverride: row.manualOverride,
    };
}

export function formatSfdcSyncDisplay(row: AgreementRow | null): string {
    if (!row) return '—';
    const raw = String(row.sfdcSyncDisplay || '').trim();
    return raw && raw !== '—' ? raw : 'Not synced';
}
